import Echo from 'laravel-echo'
import type { ConnectionStatus } from 'laravel-echo'
import Pusher from 'pusher-js'
import { api } from './api'
import { getStoredToken } from './authSession'
import { backendUrl } from './config'

/**
 * Client temps réel du back-office GoFlash — Laravel Echo sur Pusher.
 *
 * Configuration de référence (goflash-backend), reproduite à l'identique ici :
 *  - `.env` : `BROADCAST_CONNECTION=pusher`, `PUSHER_APP_KEY`,
 *    `PUSHER_APP_CLUSTER=mt1`, `PUSHER_PORT=443`, `PUSHER_SCHEME=https` ;
 *  - `config/broadcasting.php` : connection `pusher` (driver `pusher`) ;
 *  - `resources/js/echo.js` : `new Echo({ broadcaster: 'pusher', key, cluster, forceTLS: true })`.
 *
 * Laravel Reverb n'est PAS utilisé par ce backend : aucune variable `REVERB_*`
 * n'y est définie, donc aucune variable `VITE_REVERB_*` ne doit être lue ici.
 *
 * Particularités du panneau d'administration :
 *  - l'API est stateless (jetons Bearer Sanctum) : l'autorisation des canaux
 *    privés passe par `/broadcasting/auth` avec le jeton courant, relu à chaque
 *    requête (`customHandler`) afin de survivre à la rotation des jetons
 *    (services/authSession) ;
 *  - les canaux de course/chauffeur du domaine (`rides.{id}`, `driver.{id}`)
 *    sont réservés aux parties de la course ; le staff back-office écoute donc
 *    les canaux `admin.*` déclarés dans `routes/channels.php`.
 */

export type EchoInstance = Echo<'pusher'>

/** État de connexion : ceux de Pusher, plus l'état « non configuré ». */
export type RealtimeStatus = ConnectionStatus | 'unconfigured'

declare global {
  interface Window {
    Pusher: typeof Pusher
    Echo?: EchoInstance
  }
}

// Pusher doit être joignable globalement : laravel-echo instancie le client via
// `window.Pusher` (même approche que goflash-backend/resources/js/echo.js).
window.Pusher = Pusher

const env = import.meta.env

/** Clé publique Pusher — même valeur que `PUSHER_APP_KEY` côté backend. */
export const pusherAppKey: string = env.VITE_PUSHER_APP_KEY ?? ''

/** Cluster Pusher — `mt1` par défaut dans le `.env` du backend. */
export const pusherCluster: string = env.VITE_PUSHER_APP_CLUSTER || 'mt1'

/** Schéma de connexion (https en SaaS Pusher, port 443). */
const pusherScheme: string = env.VITE_PUSHER_SCHEME || 'https'

/**
 * Serveur Pusher explicite (auto-hébergement type soketi) : uniquement lorsque
 * `VITE_PUSHER_HOST` est renseigné. En SaaS Pusher, l'hôte est déduit du
 * cluster par pusher-js — comme côté backend où `PUSHER_HOST` est vide.
 */
const pusherHost: string | null = env.VITE_PUSHER_HOST || null
const pusherPort: number | null = env.VITE_PUSHER_PORT ? Number(env.VITE_PUSHER_PORT) : null

/**
 * Endpoint d'autorisation des canaux privés.
 * Défaut : route Laravel standard `/broadcasting/auth`. Côté backend
 * (`withBroadcasting()` dans `bootstrap/app.php`), cette route est protégée par
 * `auth:sanctum` : c'est le jeton Bearer courant qui y est évalué, aucun cookie
 * de session n'est requis. Surchargeable via `VITE_BROADCASTING_AUTH_URL` si le
 * backend expose un jour une route dédiée.
 */
export const broadcastingAuthEndpoint: string =
  env.VITE_BROADCASTING_AUTH_URL || `${backendUrl}/broadcasting/auth`

/**
 * Noms des canaux privés du back-office.
 * Ils correspondent exactement à ceux déclarés (et autorisés au staff) dans
 * `goflash-backend/routes/channels.php`.
 *
 * `admin.ride.{id}` et `admin.driver.{id}` sont indexés sur l'identifiant
 * technique (clé primaire) car les événements du domaine diffusent sur cet
 * identifiant — les pages admin convertissent donc l'UUID de leur URL via le
 * champ `numeric_id` exposé par l'API (`RideResource`, `DriverController`).
 */
export const staffChannels = {
  dashboard: 'admin.dashboard',
  rides: 'admin.rides',
  drivers: 'admin.drivers',
  user: (userId: number | string): string => `admin.user.${userId}`,
  ride: (rideId: number | string): string => `admin.ride.${rideId}`,
  driver: (driverId: number | string): string => `admin.driver.${driverId}`,
} as const

/**
 * Canal privé des notifications personnelles.
 *
 * `NotificationSent::broadcastOn()` diffuse sur `driver.{id}` lorsque le
 * destinataire a le rôle `driver`, sinon sur `client.{id}` — les membres du
 * staff back-office reçoivent donc leurs notifications sur `client.{id}`
 * (canal autorisé à son propriétaire dans `routes/channels.php`).
 */
export function personalChannel(userId: number | string, isDriver: boolean): string {
  return `${isDriver ? 'driver' : 'client'}.${userId}`
}

/**
 * Noms d'événements réellement diffusés par le backend (`broadcastAs()`).
 * Le préfixe `.` est obligatoire pour tout événement renommé côté serveur :
 * sans lui, Echo chercherait `App\Events\<nom>` et n'écouterait rien.
 */
export const realtimeEvents = {
  rideRequested: '.ride.requested',
  rideAccepted: '.ride.accepted',
  rideCancelled: '.ride.cancelled',
  rideStatusUpdated: '.ride.status_updated',
  ridePaymentInitiated: '.ride.payment_initiated',
  ridePaymentConfirmed: '.ride.payment_confirmed',
  driverStatusUpdated: '.driver.status_updated',
  driverLocationUpdated: '.driver.location_updated',
  notificationSent: '.notification.sent',
  /**
   * Agrégats du tableau de bord périmés — émis par
   * `App\Events\DashboardStatsUpdated` (canal `admin.dashboard`) à chaque
   * changement de course ou de chauffeur. Le panneau relance ensuite ses
   * propres requêtes HTTP : le signal ne transporte aucune statistique.
   */
  statsUpdated: '.stats.updated',
} as const

/** Tous les événements du cycle de vie d'une course, hors `ride.requested`. */
export const rideLifecycleEvents: readonly string[] = [
  realtimeEvents.rideAccepted,
  realtimeEvents.rideStatusUpdated,
  realtimeEvents.rideCancelled,
  realtimeEvents.ridePaymentInitiated,
  realtimeEvents.ridePaymentConfirmed,
]

let echoInstance: EchoInstance | null = null

/** La clé publique Pusher est-elle renseignée ? */
export function isRealtimeConfigured(): boolean {
  return pusherAppKey.length > 0
}

/** Données renvoyées par `/broadcasting/auth`, au format attendu par pusher-js. */
type ChannelAuthorizationData = {
  auth: string
  channel_data?: string
  shared_secret?: string
}

/** Paramètres fournis par pusher-js : identifiant du socket et canal demandé. */
type ChannelAuthorizationParams = {
  socketId: string
  channelName: string
}

type ChannelAuthorizationCallback = (
  error: Error | null,
  data: ChannelAuthorizationData | null,
) => void

/**
 * Autorisation d'un canal privé/presence (équivalent du `POST /broadcasting/auth`).
 *
 * La requête passe par le client HTTP partagé (`services/api`) et hérite donc
 * de l'intercepteur de session : un 401 déclenche la rotation du jeton Sanctum
 * puis le rejeu automatique de la requête.
 *
 * C'est indispensable ici : pusher-js émet sa propre requête XHR, hors de tout
 * intercepteur. Sans ce gestionnaire, un jeton expiré faisait échouer la
 * souscription (`pusher:subscription_error`) et seul un rechargement de page
 * rétablissait le temps réel.
 */
async function authorizeChannel(
  params: ChannelAuthorizationParams,
  callback: ChannelAuthorizationCallback,
): Promise<void> {
  try {
    const response = await api.post<ChannelAuthorizationData>(broadcastingAuthEndpoint, {
      socket_id: params.socketId,
      channel_name: params.channelName,
    })

    callback(null, response.data)
  } catch (error) {
    callback(
      error instanceof Error
        ? error
        : new Error(`Autorisation refusée pour le canal ${params.channelName}.`),
      null,
    )
  }
}

/**
 * Nombre d'écouteurs par canal.
 *
 * Plusieurs composants peuvent observer le même canal (layout + page). La
 * souscription n'est libérée qu'au dernier désabonnement : sans ce compteur,
 * le démontage d'une page couperait les écouteurs encore utiles du layout.
 */
const channelUsage = new Map<string, number>()

/** Déclare un écouteur supplémentaire sur un canal. */
export function retainChannel(name: string): string {
  channelUsage.set(name, (channelUsage.get(name) ?? 0) + 1)

  return name
}

/** Libère un écouteur ; le canal n'est quitté qu'au dernier. */
export function releaseChannel(name: string): void {
  const remaining = (channelUsage.get(name) ?? 1) - 1

  if (remaining > 0) {
    channelUsage.set(name, remaining)

    return
  }

  channelUsage.delete(name)
  echoInstance?.leave(name)
}

/**
 * Instance Echo partagée (singleton) connectée à Pusher.
 *
 * Retourne `null` tant que la clé Pusher ou la session manque : aucune
 * connexion n'est ouverte pour un visiteur non authentifié.
 */
export function getEcho(): EchoInstance | null {
  if (!isRealtimeConfigured() || !getStoredToken()) {
    return null
  }

  if (!echoInstance) {
    echoInstance = new Echo<'pusher'>({
      broadcaster: 'pusher',
      key: pusherAppKey,
      cluster: pusherCluster,
      forceTLS: pusherScheme === 'https',
      enabledTransports: ['ws', 'wss'],
      // En SaaS Pusher, l'hôte et le port sont déduits du cluster : on ne les
      // surcharge que pour un serveur Pusher auto-hébergé (soketi…).
      ...(pusherHost
        ? {
            wsHost: pusherHost,
            wsPort: pusherPort ?? 443,
            wssPort: pusherPort ?? 443,
          }
        : {}),
      channelAuthorization: {
        // Le gestionnaire personnalisé remplace le transport `ajax` de
        // pusher-js : la requête d'autorisation est émise par le client HTTP de
        // l'application et bénéficie donc de la rotation automatique du jeton.
        customHandler: authorizeChannel,
      },
    })

    // Parité avec le backend : `window.Echo` reste inspectable en console.
    window.Echo = echoInstance
  }

  return echoInstance
}

/** Ferme la connexion temps réel (déconnexion, fin de session…). */
export function disconnectEcho(): void {
  channelUsage.clear()

  if (echoInstance) {
    echoInstance.disconnect()
    echoInstance = null
    window.Echo = undefined
  }
}

/** État de connexion courant (« unconfigured » si aucune connexion ouverte). */
export function realtimeConnectionStatus(): RealtimeStatus {
  return echoInstance ? echoInstance.connectionStatus() : 'unconfigured'
}

/**
 * Observe les changements d'état de la connexion Pusher.
 * Retourne la fonction de désabonnement.
 */
export function onRealtimeConnectionChange(
  callback: (status: RealtimeStatus) => void,
): () => void {
  const instance = getEcho()

  if (!instance) {
    callback('unconfigured')

    return () => undefined
  }

  callback(instance.connectionStatus())

  return instance.connector.onConnectionChange(callback)
}

